import { Button, Input, Table, Tooltip } from 'antd'
import { ColumnProps as AntdColumnProps, TableProps } from 'antd/es/table'
import * as React from 'react'
import { useState } from 'react'
import Highlighter from 'react-highlight-words'

import { DocumentType } from './dtos'
import { Icons, SearchIcon } from '../icons'
import * as styles from './OmniSearch.module.less'

enum WbsComponentType {
    'WBS' = 0,
    'Control Account' = 1,
    'Work Package' = 2
}

export interface ColumnProps<T = any> extends AntdColumnProps<T> {
    filterType: 'search' | 'none'
}

export interface OmniSearchProps {
    columns: ColumnProps[]
    dataSource: any[]
    getColumnSearchProps?: any
    onRow: any
    rowKey?: string
    scrollX?: any
    scrollY?: any
    selectedKey?: string
}

export const getColumnSearchProps = (
    col: any,
    searchText: any,
    setSearchText: any,
    expandedRowKeys: string[] | number[]
) => {
    const dataIndex = col.key
    const handleSearch = (selectedKeys: any, confirm: any) => {
        confirm()
        setSearchText(selectedKeys[0])
    }

    const handleReset = (clearFilters: any) => {
        clearFilters()
        setSearchText('')
    }

    // console.log('expandedRowKeys', expandedRowKeys)

    return {
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters
        }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={() => handleSearch(selectedKeys, confirm)}
                    icon={<Icons.Application.SearchIcon />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button
                    onClick={() => handleReset(clearFilters)}
                    size="small"
                    style={{ width: 90 }}
                >
                    Reset
                </Button>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchIcon style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value: any, record: any) => {
            const recIndex = record[dataIndex] || record.data[dataIndex]

            // work on recIndex, it holds the answer.
            // console.log('_______________onFilter____________')
            // console.log('value searched:', value)
            // console.log('record', record)
            // console.log('record JSON', JSON.stringify(record))
            // console.log('recIndex', recIndex)

            // if any descendant has the value, then return true
            return JSON.stringify(record)
                .toLowerCase()
                .includes(value.toLowerCase())
        },

        render: (text: any, record: any) => {
            // const isExpanded: boolean = expandedRowKeys.indexOf(record.id || record.key) > -1

            // Prevent errors in case of no children:
            if (record.children === undefined) record.children = []

            // Find if WBS Component is a root, mid or leaf
            let wbsComponentType: WbsComponentType = WbsComponentType['WBS']
            if (record.type === DocumentType.WbsComponent) {
                const wbsId: string = record.data.wbsId
                if (wbsId.indexOf('.') === -1) {
                    wbsComponentType = WbsComponentType['WBS']
                } else {
                    let isControlAccount: boolean = false
                    if (
                        record.children.find(
                            (c: any) => c.type === DocumentType.WbsComponent
                        ) !== undefined
                    ) {
                        isControlAccount = true
                    }

                    wbsComponentType = isControlAccount
                        ? WbsComponentType['Control Account']
                        : WbsComponentType['Work Package']
                }
            }

            // Find the right type of icon for each record
            let icon: React.ReactNode
            if (record.type === DocumentType.Organization) {
                icon = <Icons.OrganizationIcon />
            } else if (record.type === DocumentType.Portfolio) {
                icon = <Icons.Ppm.PortfolioIcon />
            } else if (record.type === DocumentType.Project) {
                icon = <Icons.Ppm.ProjectIcon />
            } else if (record.type === DocumentType.WbsComponent) {
                if (wbsComponentType === WbsComponentType['WBS']) {
                    icon = <Icons.Wbs.WbsIcon />
                } else if (
                    wbsComponentType === WbsComponentType['Work Package']
                ) {
                    icon = <Icons.Wbs.WorkPackageIcon />
                } else {
                    icon = <Icons.Wbs.WbsControlAccountIcon />
                }
            }

            // Formatting of the record's title.
            const titleFontStyle =
                record.type === DocumentType.Organization ||
                record.type === DocumentType.Portfolio // ||
                    ? // record.type === DocumentType.Project
                      'bold'
                    : 'normal'

            let tooltipTitle: React.ReactNode =
                record.type === DocumentType.WbsComponent
                    ? `${WbsComponentType[wbsComponentType]}: ${record.title}`
                    : `${DocumentType[record.type]}: ${record.title}`

            return (
                <Tooltip mouseEnterDelay={0.5} title={tooltipTitle}>
                    <span>
                        {icon}
                        {record.type === DocumentType.WbsComponent && (
                            <span
                                style={{
                                    fontSize: '.7rem',
                                    marginLeft: '.6rem'
                                }}
                            >
                                {record.data.wbsId}.
                            </span>
                        )}

                        <span
                            style={{
                                marginLeft: '.6rem',
                                fontWeight: titleFontStyle
                            }}
                        >
                            {/* <span style={{ marginLeft: '.6rem', fontWeight: record.type === 4 ? 'bold' : 'normal' }}> */}
                            <Highlighter
                                highlightStyle={{
                                    backgroundColor: '#ffc069',
                                    padding: 0
                                }}
                                searchWords={[searchText]}
                                autoEscape
                                textToHighlight={text}
                            />
                        </span>
                    </span>
                </Tooltip>
            )
        }
    }
}

const toTableDataSource = (dataSource: any) => {
    dataSource.map((node: any) => {
        if (node.children) {
            if (node.children.length > 0) {
                toTableDataSource(node.children)
            } else {
                node.children = undefined // not using `delete Object myProperty` as it is ~100 times slower.
            }
        }
    })
}

const toTableColumns = (
    columns: any,
    searchText: any,
    setSearchText: any,
    expandedRowKeys: string[] | number[],
    customGetColumnSearchProps?: any
): ColumnProps[] => {
    const getColumnSearch = customGetColumnSearchProps
        ? customGetColumnSearchProps
        : getColumnSearchProps

    return columns.map((c: any) => {
        let col
        const co: any = c

        co.filterType === 'search'
            ? (col = {
                  ...c,
                  ...getColumnSearch(
                      c,
                      searchText,
                      setSearchText,
                      expandedRowKeys
                  )
              })
            : (col = c)
        return col
    })
}

export const OmniSearch: React.FC<OmniSearchProps> = (props) => {
    const [searchText, setSearchText] = useState<string>('')
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[] | number[]>(
        []
    )

    const scrollY: string = props.scrollY // || document.getElementById(props.scrollYClass).

    const columns = toTableColumns(
        props.columns,
        searchText,
        setSearchText,
        expandedRowKeys,
        props.getColumnSearchProps
    )

    const onExpand = (expanded: boolean, record: any) => {
        // console.log('onExpand', expanded)
    }

    // Haven't been able to clone object successfully
    const dataSource = JSON.parse(JSON.stringify(props.dataSource))
    // Also, this is a very very bad way to do it:
    toTableDataSource(dataSource)

    const onExpandedRowsChange = (expandedRowKeys: string[] | number[]) => {
        setExpandedRowKeys(expandedRowKeys)
    }

    return (
        <Table
            {...props}
            onExpand={onExpand}
            // @ts-ignore
            onExpandedRowsChange={onExpandedRowsChange}
            className={styles.omniSearch}
            bordered={false}
            columns={columns}
            dataSource={dataSource}
            defaultExpandAllRows={true}
            // @ts-ignore
            rowSelection={{ selectedRowKeys: [props.selectedKey] }}
            onRow={props.onRow}
            pagination={false}
            scroll={{ y: `${scrollY}px` }}
            size="small"
            showHeader={true}
        />
    )
}
